import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';

import type { Rarity } from '__generated__/globalTypes';
import { ConditionalWrapper } from 'atoms/layout/ConditionalWrapper';
import type { ValidWidths } from 'atoms/ui/ResponsiveImg';
import { proxyUrl } from 'atoms/ui/ResponsiveImg';
import { Video } from 'atoms/ui/Video';
import { CardImg, CardImgLoadingWrapper } from 'components/card/CardImg';
import { CardMeta } from 'components/card/CardMeta';
import { getAnimatedCardVideoSources } from 'lib/cards';
import { isType } from 'lib/gql';

import { Energized } from '../Energized';
import { MountingAnimation } from './MountingAnimation';
import { UnmountingAnimation } from './UnmountingAnimation';
import type {
  Card_anyCard,
  Card_commonPlayer,
  Card_so5Appearance,
} from './__generated__/index.graphql';

const RoundedVideo = styled(Video)`
  overflow: clip;
  aspect-ratio: var(--card-aspect-ratio);
  content-visibility: auto;
  contain: content;
`;

interface ClickHandlerProps {
  onClick?: () => void;
}
interface IProps {
  card:
    | Card_anyCard
    | Card_so5Appearance
    | (Card_commonPlayer & { rarityTyped?: Rarity });
  width?: ValidWidths;
  energized?: boolean;
  animations?: { mounting?: boolean; unmouting?: boolean };
  unmounting?: boolean;
  lazy?: boolean;
}

export const Card = (props: IProps & ClickHandlerProps) => {
  const [loaded, setLoaded] = useState(false);
  const {
    card,
    width = 320,
    onClick,
    energized,
    animations,
    unmounting,
    lazy = true,
  } = props;
  const { pictureUrl, anyPlayer, rarityTyped } = card;

  const videoSources = isType(card, 'CommonPlayer')
    ? []
    : getAnimatedCardVideoSources(card, width);

  const withMeta = !(
    isType(card, 'So5Appearance') || isType(card, 'MissedAppearance')
  );

  const isEnergized =
    energized ?? (isType(card, 'So5Appearance') && card.energized);
  const isSealed = 'sealed' in card && card.sealed;

  const mediaElement = document.createElement('video');
  const useVideoBackground =
    videoSources.filter(e => mediaElement.canPlayType(e.type) !== '').length >
      0 && !isSealed;

  return (
    <CardImgLoadingWrapper loaded={loaded}>
      {!loaded && !!withMeta && <CardMeta card={card} />}
      <ConditionalWrapper
        Wrapper={MountingAnimation}
        wrap={!!animations?.mounting}
        props={{ id: card.anyPlayer.slug }}
      >
        <ConditionalWrapper
          Wrapper={UnmountingAnimation}
          wrap={!!animations?.unmouting && !!unmounting}
        >
          <ConditionalWrapper
            wrap={isEnergized}
            Wrapper={Energized}
            props={{ variant: rarityTyped }}
          >
            <div>
              {useVideoBackground ? (
                <RoundedVideo
                  sources={videoSources}
                  autoPlay
                  loop
                  muted
                  onLoadedData={() => setLoaded(true)}
                  poster={
                    pictureUrl
                      ? proxyUrl(pictureUrl, { cropWidth: width })
                      : undefined
                  }
                  playsInline
                  hideMutedIcon
                  style={{
                    mask: pictureUrl
                      ? `url(${proxyUrl(pictureUrl, { cropWidth: width })}) center / cover no-repeat`
                      : 'none',
                  }}
                />
              ) : (
                <CardImg
                  {...(lazy ? { loading: 'lazy' } : {})}
                  src={pictureUrl || undefined}
                  alt={`${anyPlayer.displayName}${rarityTyped ? ` - ${rarityTyped}` : ''}`}
                  draggable={false}
                  onLoad={() => {
                    setLoaded(true);
                  }}
                  onClick={onClick}
                  width={width}
                />
              )}
            </div>
          </ConditionalWrapper>
        </ConditionalWrapper>
      </ConditionalWrapper>
    </CardImgLoadingWrapper>
  );
};
Card.displayName = 'Card';

Card.fragments = {
  anyCard: gql`
    fragment Card_anyCard on AnyCardInterface {
      slug
      pictureUrl
      rarityTyped
      sealed
      anyPlayer {
        slug
        displayName
      }
      ...CardMeta_AnyCardInterface
      ...getAnimatedCardVideoSources_anyCardInterface
    }
    ${CardMeta.fragments.AnyCardInterface}
    ${getAnimatedCardVideoSources.fragments.anyCardInterface}
  ` as TypedDocumentNode<Card_anyCard>,
  so5Appearance: gql`
    fragment Card_so5Appearance on So5AppearanceInterface {
      id
      energized
      pictureUrl
      rarityTyped: rarity
      anyPlayer {
        slug
        displayName
      }
      ...getAnimatedCardVideoSources_so5Appearance
    }
    ${getAnimatedCardVideoSources.fragments.so5Appearance}
  ` as TypedDocumentNode<Card_so5Appearance>,
  commonPlayer: gql`
    fragment Card_commonPlayer on CommonPlayer {
      id
      pictureUrl
      anyPlayer {
        slug
        displayName
        ...CardMeta_anyPlayerInterface
      }
    }
    ${CardMeta.fragments.anyPlayerInterface}
  ` as TypedDocumentNode<Card_commonPlayer>,
};
